/**
 * @ComponentFor CheckoutPageViewModel
 */
import React from 'react';
import { styled } from '@glitz/react';
import { addUserLog, currentUrl, scrollTo, translate } from '@polarnopyret/scope';
import { EpiProperty, epiPropertyValue } from '@polarnopyret/scope-episerver';
import { Basic, Form } from 'Shared/PageLayout';
import Page from 'Shared/Page';
import { GridWrap } from 'Shared/PageLayout';
import { connectWithFeedback, ConnectPropType as FeedbackPropType, ButtonTheme } from 'Shared/Button/Feedback';
import FullCart from 'Checkout/FullCart';
import Summary from 'Checkout/Summary';
import Voucher from 'Checkout/Vouchers';
import DiscountCode from 'Checkout/DiscountCode';
import GiftCard from 'Checkout/GiftCards';
import {
  sendPendingCheckoutRequests,
  addGiftCardCode,
  removeGiftCardCode,
  addDiscountCode,
  removeDiscountCode,
  setApplyMembership,
  onKlarnaShippingOptionChange,
  VoucherType,
  addVoucher,
  removeVoucher,
  setShippingMethod,
  setAddresses
} from 'Checkout/action-creators';
import ShippingMethods from 'Checkout/Shipping/ShippingMethods';
import AdyenPayment from 'Checkout/Payments/Adyen';
import { openAccountBox, AccountBoxScene } from 'SiteLayout/AccountBox/action-creators';
import connect from 'Shared/connect';
import CheckoutType from './CheckoutPageViewModel.type';
import CheckoutUpdateResultType from './CheckoutUpdateResult.type';
import ShippingMethodViewModelType from 'Checkout/Shipping/ShippingMethodViewModel.type';
import AddressType from './Address.type';
import { CartType } from 'Shared/State';
import { isCompact } from 'Shared/Viewport';
import InteractivePaymentGatewayAdapter from '@polarnopyret/interactive-payment-gateway-adapter';
import CheckoutError from './CheckoutError';
import Html from 'Shared/Partials/PropertyTypes/Html';
import { H1, H2, Paragraph, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import Price from 'Pricing/Price';
import RecommendationsList from 'Product/SwipeableList';
import { Checkbox } from 'Shared/Fields/Toggle';
import { SolidButton, LinkButton } from 'Shared/TextButtons';
import { Down24x40 as Swirl } from 'Shared/Icons/ArrowSwirl';
import { Down12x12 } from 'Shared/Icons/Arrow';
import { createGridClasses, createCenterGridClass, createOffsetGridClassLarge, GridSize } from 'Shared/grid';
import { selectDeviceImageFromProperty } from 'Shared/utils';
import { setImageBackground, Preset } from 'Shared/image-background';
import * as style from 'Shared/Style';
import CartValidationItem from 'Cart/Models/CartValidationItem.type';
import PaymentType from './PaymentModel+PaymentType.type';
import CheckoutUpdateResult from './CheckoutUpdateResult.type';
import CustomerDetails from 'Shared/CustomerDetails';
import Link from 'Shared/Link';

type ConnectedStateType = {
  cart: CartType;
  currentBreakpoint: number;
  language: string;
  isLoggedIn: boolean;
};

type ConnectedDispatchType = {
  setShippingMethod: (method: ShippingMethodViewModelType) => Promise<CheckoutUpdateResult>;
  setAddresses: (shippingAddress: AddressType, billingAddress: AddressType, useSeparateBillingAddress: boolean) => Promise<CheckoutUpdateResult>;
  onKlarnaShippingOptionChange: (args: KlarnaCheckoutShippingOptionChangeEventArgs) => Promise<void>;
  sendPendingCheckoutRequests: () => Promise<void>;
  addVoucher: (id: string, type: VoucherType) => Promise<CheckoutUpdateResultType>;
  removeVoucher: (id: string, type: VoucherType) => Promise<void>;
  addGiftCardCode: (cardNumber: string) => Promise<CheckoutUpdateResultType>;
  removeGiftCardCode: (code: string) => Promise<void>;
  addDiscountCode: (code: string) => Promise<CheckoutUpdateResultType>;
  removeDiscountCode: (code: string) => Promise<void>;
  setApplyMembership: (value: boolean) => Promise<void>;
  openAccountBox: (scene?: AccountBoxScene) => void;
};

type PropType = CheckoutType & ConnectedStateType & ConnectedDispatchType;

type StateType = {
  paymentWidgetData: string;
  paymentSessionId: string;
  shippingSelector: boolean;
  paymentType: PaymentType;
  storeCartId?: number;
  showError?: boolean;
  paymentErrors?: string[];
  shouldKlarnaLock: boolean;
  shippingLock: boolean;
  activeCheckoutStep: number;
  completedCheckoutStep: number;
  validationItems: CartValidationItem[];
  customerAddresses: {
    shippingAddress: AddressType;
    billingAddress: AddressType;
    separateShippingAddress: boolean;
  };
  allowCheckoutCompletion: boolean;
  customerDetailsError: string;
  shippingOptionError: string;
  shippingOptionInvalid: boolean;
};

const ShippingNote = styled.div({
  backgroundColor: style.colors.brandColors.yellow,
  color: style.colors.monochrome.black,
  padding: { xy: '8px' },
  marginBottom: '10px',
  display: 'flex',
  ['@media ' + style.mediaMinLarge]: {
    padding: { xy: '16px' },
  },
});

const BecomeMember = styled(Checkbox, {
  fontWeight: 'bold',
  textTransform: 'uppercase',
  flexDirection: 'row',
  whiteSpace: 'nowrap',
  ['@media ' + style.mediaUpToLarge]: {
    alignSelf: 'center',
  },
});

const RegisterContentWrap = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  border: {
    xy: { color: style.colors.brandColors.green, width: 2, style: 'solid' },
  },
  padding: {
    xy: 16,
  },
  borderRadius: 8,
  marginBottom: '16px',
  ['@media ' + style.mediaUpToLarge]: {
    flexDirection: 'column',
  },
});

const RegisterContentHeadingWrap = styled.div({
  ['@media ' + style.mediaMinLarge]: {
    flexBasis: '70%',
  },
});

const RegisterContentHeading = styled(H2, {
  marginBottom: '12px',
});

const RegisterContentBody = styled.div({
  ['@media ' + style.mediaUpToLarge]: {
    textAlign: 'center',
  },
  ['@media ' + style.mediaMinLarge]: {
    alignSelf: 'end',
    textAlign: 'right',
    flexBasis: '30%',
  },
});

const BecomeMemberWrap = styled.span({
  border: {
    xy: { color: style.colors.brandColors.green, width: 2, style: 'solid' },
  },
  color: style.colors.brandColors.green,
  borderRadius: '30px',

  ['@media ' + style.mediaUpToLarge]: {
    display: 'flex',
    flexDirection: 'column',
    height: '40px',
    lineHeight: '38px',
    padding: {
      left: '20px',
      right: '20px',
    },
  },
  ['@media ' + style.mediaMinLarge]: {
    display: 'inline-block',
    height: '48px',
    lineHeight: '46px',
    padding: {
      left: '24px',
      right: '24px',
    },
  },
});

const MemberContentWrap = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  backgroundColor: style.colors.brandColors.green,
  color: style.colors.monochrome.white,
  padding: {
    xy: 16,
  },
  borderRadius: 8,
  marginBottom: '16px',
  ['@media ' + style.mediaUpToLarge]: {
    flexDirection: 'column',
  },
});

const MemberContentHeadingWrap = styled.div({
  ['@media ' + style.mediaMinLarge]: {
    flexBasis: '70%',
  },
});

const MemberContentHeading = styled(H2, {
  marginBottom: '12px',
});

const MemberContentLoginWrap = styled.div({
  ['@media ' + style.mediaMinLarge]: {
    flexBasis: '30%',
    textAlign: 'right',
  },
});

const MemberContentLogin = styled(SolidButton, {
  ['@media ' + style.mediaUpToLarge]: {
    marginTop: '24px',
    textAlign: 'center',
  },
});

const SummaryWrap = styled.div({
  backgroundColor: style.colors.monochrome.extraLightGrey,
  color: style.colors.monochrome.black,
  padding: {
    xy: 16,
  },
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
});

const SummaryTop = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '16px',
});

const SummaryTopCount = styled.span({
  textTransform: 'uppercase',
  fontWeight: 500,
});

const SummaryPaymentWrap = styled.div({
  marginTop: '18px',
});

const SummarySummary = styled(Summary, {
  borderTop: {
    width: '2px',
    style: 'solid',
    color: style.colors.monochrome.black,
  },
  marginTop: '18px',
  paddingTop: '18px',
});

const ExtraInformationWrap = styled.div({
  backgroundColor: style.colors.brandColors.yellowAlternative,
  color: style.colors.monochrome.black,
  padding: {
    xy: 8,
  },
  margin: {
    xy: 0,
  },
  borderRadius: 8,
  fontSize: '14px',
  marginTop: '12px',
  overflow: 'hidden',
  textAlign: 'center',
  ['@media ' + style.mediaMinLarge]: {
    marginTop: '16px',
  },
});

const GotoPay = styled(SolidButton, {
  marginTop: '12px',
  display: 'flex',
  ['@media ' + style.mediaMinLarge]: {
    marginTop: '16px',
  },
});

const GotoPayIcon = styled(Down12x12, {
  marginLeft: '12px',
});

const RecommendationsWrap = styled.div({
  marginTop: '48px',
  marginBottom: '48px',
  ['@media ' + style.mediaUpToLarge]: {
    marginRight: '-12px',
  },
});

const EmptyCartWrap = styled.div({
  marginTop: '-50px',
  overflow: 'hidden',
  background: {
    size: 'cover',
    positionX: 'center',
    positionY: 'center',
  },
  height: '100vh',
});

const EmptyCartGridWrap = styled(GridWrap, {
  height: '100%',
  alignItems: 'center',
  position: 'relative',
});

const EmptyCartInnerWrap = styled.div({
  textAlign: 'center',
});

const EmptyCartHeading = styled(H1, {
  color: style.colors.monochrome.white,
  marginBottom: '16px',
});

const EmptyCartParagraph = styled(Paragraph, {
  color: style.colors.monochrome.white,
  marginBottom: '16px',
});

const EmptyCartButtonWrap = styled.div({
  width: '100%',
  left: 0,
  textAlign: 'center',
  position: 'absolute',
  bottom: '40px',
});

const EmptyCartLink = styled(LinkButton, {
  display: 'inline-flex',
});

const FormContent = styled.div({
});

const FormContentInner = styled.div({
  paddingTop: '24px',
});

const FormContentHeading = styled(H2, {
  marginBottom: '16px',
});

const CheckoutStepHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between'
});

const CheckoutStepTitle = styled(H2, {
  marginBottom: '16px',
  paddingBottom: '8px',
  // borderBottom: {
  //   color: style.colors.monochrome.a11ygrey,
  //   style: 'solid',
  //   width: '1px'
  // }
});

const CheckoutStepEdit = styled(Link, {
  cursor: 'pointer'
});

const CheckoutStepContent = styled.div({
  marginBottom: '32px',
  position: 'absolute',
  visibility: 'hidden',
  height: '0px'
});

const PaymentWrapper = styled(Basic, {
  marginTop: '64px',
  ['@media ' + style.mediaMinLarge]: {
    marginTop: '32px',
  },
});

const PaymentWrapperAdvanced = styled(PaymentWrapper, {
  backgroundColor: style.colors.monochrome.extraLightGrey,
  padding: {
    x: '24px',
    y: '32px'
  },
  marginBottom: '24px',
  borderRadius: '16px'
});

const RightCol = styled.div({
  paddingTop: '24px',
});

const RightColSticky = styled.div({
  position: 'sticky',
  top: '16px',
  paddingBottom: '16px',
});

/* const ErrorText = styled.span({
  display: 'block',
});
 */

const w = window as any as {
  _klarnaCheckout(f: (api: any) => void): void;
};

export type KlarnaCheckoutShippingOptionChangeEventArgs = {
  description: string;
  id: string;
  name: string;
  price: number;
  promo: string;
  tax_amount: number;
  tax_rate: number;
};

class CheckoutPage extends React.Component<PropType & FeedbackPropType, StateType> {
  formElement: HTMLFormElement;
  paymentWidgetElement: HTMLElement;
  shippingRef: HTMLElement;
  paymentRef: HTMLElement;
  invalidListener: () => void;
  interactiveGateway: InteractivePaymentGatewayAdapter;
  constructor(props: PropType & FeedbackPropType) {
    super(props);
    this.state = {
      paymentWidgetData: props.payment.paymentWidgetData,
      paymentSessionId: props.payment.sessionId,
      paymentType: props.payment.type,
      shippingSelector: props.shipping.shippingSelector,
      showError: false,
      shouldKlarnaLock: false,
      shippingLock: false,
      activeCheckoutStep: 1,
      completedCheckoutStep: 0,
      validationItems: props.cart.validationItems,
      customerAddresses: {
        shippingAddress: props.addresses?.shippingAddress,
        billingAddress: props.addresses?.billingAddress,
        separateShippingAddress: props.addresses?.separateShippingAddress,
      },
      allowCheckoutCompletion: props.payment?.widgetEnabled,
      customerDetailsError: null,
      shippingOptionError: null,
      shippingOptionInvalid: false
    };
  }

  componentWillReceiveProps(nextProps: PropType & FeedbackPropType) {
    if (nextProps.payment) {
      this.setState({ allowCheckoutCompletion: nextProps.payment.widgetEnabled })

      if (nextProps.payment.paymentWidgetData) {
        this.setState({ paymentWidgetData: nextProps.payment.paymentWidgetData });
      }

      if (nextProps.payment.sessionId) {
        this.setState({ paymentSessionId: nextProps.payment.sessionId });
      }
    }

    if (nextProps.cart.validationItems && nextProps.cart.validationItems.length > 0) {
      this.setState({ validationItems: nextProps.cart.validationItems });
    }

    if (nextProps.addresses) {
      this.setState({
        customerAddresses: {
          shippingAddress: nextProps.addresses.shippingAddress,
          billingAddress: nextProps.addresses.billingAddress,
          separateShippingAddress: nextProps.addresses.separateShippingAddress
        }
      });
    }
  }
  componentDidMount() {
    if (this.formElement) {
      this.invalidListener = () => {
        addUserLog('Tried to submit invalid checkout form');
      };
      this.formElement.addEventListener('invalid', this.invalidListener, true);
    }

    if (w._klarnaCheckout) {
      w._klarnaCheckout((api) => {
        api.on({
          shipping_option_change: (args: KlarnaCheckoutShippingOptionChangeEventArgs) => {
            this.props.onKlarnaShippingOptionChange(args);
          },
          // load: (args: any) => {
          //   const shippingArgs = args?.shipping_option_change as KlarnaCheckoutShippingOptionChangeEventArgs;
          //   if (shippingArgs?.id?.length > 0) {
          //     //this.props.onShippingOptionChange(shippingArgs);
          //   }
          // },
        });
      });
    }
  }
  componentWillUnmount() {
    if (this.formElement && this.invalidListener) {
      this.formElement.removeEventListener('invalid', this.invalidListener, true);
      this.formElement = null;
      this.invalidListener = null;
    }
  }

  componentWillMount() {
    this.checkQuery();
    /* const previousPaymentMethodId = getPaymentMethodId();
    if (
      !this.props.payment.hasExplicitlySelectedPaymentMethod &&
      this.props.payment.totalSumToPay > 0 &&
      previousPaymentMethodId &&
      this.props.payment.paymentMethods.find((p) => p.id === previousPaymentMethodId)
    ) {
      this.onSelectPaymentMethod(previousPaymentMethodId);
    } */
  }

  checkQuery() {

    const url = currentUrl();
    const queryParam = 'validation_error';
    const errors = url.searchParams.getAll(queryParam);

    this.setState({ showError: false, paymentErrors: null });

    if (errors && errors.length > 0) {
      if (history.replaceState) {
        url.searchParams.delete(queryParam);
        history.replaceState(history.state, document.title, url.href);
      }

      this.setState({ showError: true });
      const processedErrors = [];

      errors.forEach((error) => {
        switch (error) {
          case 'ItemWithError':
            processedErrors.push(this.props.labels.inventoryError
              ? this.props.labels.inventoryError
              : 'Item out of stock')
            break;
          case 'InvalidPrice':
            processedErrors.push(this.props.labels.invalidPrice
              ? this.props.labels.invalidPrice
              : 'Invalid price')
            break;
          case 'paymentFailed':
            processedErrors.push(this.props.payment.labels.paymentError
              ? this.props.payment.labels.paymentError
              : 'Payment failed')
            break;
          case 'DiscountExpired':
            processedErrors.push(this.props.labels.discountExpired
              ? this.props.labels.discountExpired
              : 'Payment failed')
            break;
          default:
            processedErrors.push(error);
            break;
          //return this.setState({ paymentErrors: Array.isArray(msg) ? msg : [msg] });
        }
      })

      this.setState({ paymentErrors: processedErrors })
    }
  }

  onFormKeyPress(e: React.KeyboardEvent<HTMLElement>) {
    // Prevents form submission when the enter key is pressed in a input field
    // Only targets inputs as submit button should submit and textares should be able to add newlines.
    if (
      (e.target as HTMLElement).tagName.toLowerCase() === 'input' &&
      (e.keyCode || e.which || e.charCode || e.key || 0) === 13
    ) {
      e.preventDefault();
    }
  }

  //showErrorModal = () => this.setState({ showError: true });

  closeErrorModal = () => this.setState({ showError: false });

  onCompletePurchase(e: React.FormEvent<{}>) {
    e.preventDefault();
    if (this.props.payment.type === 'klarnaCheckout' && this.interactiveGateway && this.props.payment) {
      this.props.feedback.push(this.interactiveGateway.completePurchase(this.props.payment.totalSumToPay));
    }
  }

  // handleKlarna(lock: boolean) {
  //   // if (lock !== this.state.shouldKlarnaLock) {
  //   //   this.setState({ shouldKlarnaLock: lock });
  //   // }
  // }

  handleCustomerDetailsUpdate = async (shippingAddress: AddressType, billingAddress: AddressType, separateShippingAddress: boolean) => {
    const result = await this.props.setAddresses(separateShippingAddress ? shippingAddress : null, billingAddress, separateShippingAddress);
    if (result && result.success) {
      this.setState({ customerAddresses: { shippingAddress: separateShippingAddress ? shippingAddress : null, billingAddress, separateShippingAddress }, activeCheckoutStep: 2, completedCheckoutStep: 2 });
      if (this.props.cart.isDigital) {
        this.setState({ completedCheckoutStep: 1 });
      }
      scrollTo(this.shippingRef.offsetTop - 20, 500);
    }
    else {
      this.setState({ customerDetailsError: result.message });
    }
  };

  handleShippingOptionUpdate = async (shippingMethod: ShippingMethodViewModelType) => {
    this.setState({ shippingLock: true });
    const result = await this.props.setShippingMethod(shippingMethod);
    if (result && result.success) {
      this.setState({ completedCheckoutStep: 2, shippingOptionInvalid: false, shippingOptionError: null, shippingLock: false });
    }
    else {
      this.setState({ shippingOptionError: result.message, shippingOptionInvalid: true, shippingLock: false });
    }
  };

  render() {
    const { showError, paymentErrors, shippingLock, shouldKlarnaLock } = this.state;
    const isMobile = isCompact(this.props.currentBreakpoint);

    const showBonusChecks =
      (this.props.isLoggedIn && this.props.payment.bonusChecks && this.props.payment.bonusChecks.length > 0) ||
      (this.props.payment.bonusChecksInCart && this.props.payment.bonusChecksInCart.length > 0);

    const showSecondHandChecks =
      this.props.isLoggedIn &&
      ((this.props.payment.secondHandChecks && this.props.payment.secondHandChecks.length > 0) ||
        (this.props.payment.secondHandChecksInCart && this.props.payment.secondHandChecksInCart.length > 0));

    const cartErrors = this.props.cart.errorMessage || [];

    const combinedErrors = [].concat(paymentErrors || [], cartErrors);
    const checkoutErrorModal = (
      <CheckoutError errors={combinedErrors} toggleErrors={this.closeErrorModal} showErrors={showError} />
    );

    const discountCodeBlock = (
      <DiscountCode
        header={this.props.payment.labels.addDiscountCode}
        buttonText={this.props.payment.labels.add}
        placeholderText={this.props.payment.labels.discountPlaceholder}
        addDiscountCode={this.props.addDiscountCode}
        removeDiscountCode={this.props.removeDiscountCode}
        discountCodes={this.props.payment.discountCodes}
        errorCodes={this.props.payment.couponValidationResults}
        isMobile={isMobile}
        defaultError={this.props.payment.labels.discountCodeGenericError}
      />
    );

    const registerContent = (
      <RegisterContentWrap>
        <RegisterContentHeadingWrap>
          <RegisterContentHeading fontSize={ResponsiveSize.D24_M24}>
            {this.props.labels.registerHeading}
          </RegisterContentHeading>
          <Html html={this.props.registerBody?.html} />
        </RegisterContentHeadingWrap>
        <RegisterContentBody>
          <BecomeMemberWrap>
            <BecomeMember
              checkBoxNoneReversed
              color={style.colors.brandColors.green}
              checked={this.props.applyMembership}
              onChange={(e) => this.props.setApplyMembership((e.target as HTMLInputElement).checked)}
            >
              {this.props.labels.registerButtonText}
            </BecomeMember>
          </BecomeMemberWrap>
        </RegisterContentBody>
      </RegisterContentWrap>
    );

    const memberContent = (
      <MemberContentWrap>
        <MemberContentHeadingWrap>
          <MemberContentHeading fontSize={ResponsiveSize.D24_M24}>
            {this.props.labels.memberHeading}
          </MemberContentHeading>
          <Paragraph fontSize={ResponsiveSize.D14_M14_L130}>{this.props.labels.memberIntro}</Paragraph>
        </MemberContentHeadingWrap>
        {!this.props.isLoggedIn && (
          <MemberContentLoginWrap>
            <MemberContentLogin
              fullWidth={isMobile}
              onClick={() => this.props.openAccountBox()}
              theme={ButtonTheme.TransparentWhite}
            >
              {this.props.labels.login}
            </MemberContentLogin>
          </MemberContentLoginWrap>
        )}
      </MemberContentWrap>
    );

    const summary = (
      <>
        <SummaryWrap>
          <SummaryTop>
            <SummaryTopCount>{this.props.labels.productCount}</SummaryTopCount>
            <span>
              <Price
                sellingPrice={this.props.cart.totalPlacedPrice}
                listPrice={this.props.cart.totalListPrice}
                fontSize={16}
              />
            </span>
          </SummaryTop>
          <div>{discountCodeBlock}</div>
          {this.props.payment.giftCardsEnabled && (
            <SummaryPaymentWrap>
              <GiftCard
                header={this.props.payment.labels.addGiftCard}
                buttonText={this.props.payment.labels.add}
                placeholderText={this.props.payment.labels.giftCardPlaceholder}
                addGiftCardCode={this.props.addGiftCardCode}
                removeGiftCardCode={this.props.removeGiftCardCode}
                giftCards={this.props.payment.giftCardPayments}
                isMobile={isMobile}
                cartTotal={this.props.cart.totalPrice}
                giftCardValidation={this.props.payment.giftCardRegularExpression}
                totalPrice={this.props.payment.totalSumToPay}
                labels={{ genericError: this.props.payment.labels.giftCardGenericError }}
              />
            </SummaryPaymentWrap>
          )}

          {showBonusChecks && (
            <SummaryPaymentWrap>
              <Voucher
                header={this.props.payment.labels.addBonusCheck}
                buttonText={this.props.payment.labels.activate}
                addVoucher={this.props.addVoucher}
                removeVoucher={this.props.removeVoucher}
                voucherType={VoucherType.BonusCheck}
                vouchers={this.props.payment.bonusChecks}
                vouchersInCart={this.props.payment.bonusChecksInCart}
                cartTotal={this.props.cart.totalPrice}
                labels={{ genericError: this.props.payment.labels.genericError }}
              />
            </SummaryPaymentWrap>
          )}
          {showSecondHandChecks && (
            <SummaryPaymentWrap>
              <Voucher
                header={this.props.payment.labels.addSecondhandCheck}
                buttonText={this.props.payment.labels.activate}
                addVoucher={this.props.addVoucher} //TODO: change to something more minimal, preferably only ID
                removeVoucher={this.props.removeVoucher}
                voucherType={VoucherType.SecondHandCheck}
                vouchers={this.props.payment.secondHandChecks.map((b) => ({ ...b }))}
                vouchersInCart={
                  this.props.payment.secondHandChecksInCart?.map((b) => ({ ...b, availableAmount: b.amount })) ?? []
                } //May be null
                cartTotal={this.props.cart.totalPrice}
                labels={{ genericError: this.props.payment.labels.genericError }}
              />
            </SummaryPaymentWrap>
          )}
          <SummarySummary
            isMobile={isMobile}
            labels={this.props.labels}
            totalToPay={this.props.payment.totalSumToPay}
            totalSavings={this.props.cart.totalSavings}
            shippingTotal={this.props.cart.shippingTotal}
            payment={this.props.payment}
            cartInformation={this.props.cart.cartSumInformation}
          />
        </SummaryWrap>
        {this.props.extraInformation && <ExtraInformationWrap className="editorial-text inherit-font-size"><Html html={this.props.extraInformation?.html} /></ExtraInformationWrap>}
        <GotoPay
          theme={ButtonTheme.WhiteToBlack}
          onClick={() => scrollTo(this.paymentWidgetElement.offsetTop - 50, 500)}
          iconAfter={<GotoPayIcon />}
        >
          {this.props.payment.labels.toPaymentShortCut}
        </GotoPay>
      </>
    );

    const recommendations = (
      <RecommendationsWrap>
        <RecommendationsList
          numberOfProductsVisible={isMobile ? 1.5 : 3}
          alternativeNav
          listHeading={this.props.labels.recommendations}
          products={this.props.recommendations}
          quickAddToCart
          isCompact={isMobile}
          overflowHidden
          showNav={!isMobile}
        />
      </RecommendationsWrap>
    );

    const shippingNoteBox = (
      <>
        {this.props.backupStore?.backupInventoryOrderMessage && (
          <ShippingNote>{this.props.backupStore?.backupInventoryOrderMessage}</ShippingNote>
        )}
      </>
    );

    const emptyCartIntroText = epiPropertyValue(this.props.page.emptyCartIntro);
    const emptyCartButton = this.props.emptyCartButton;
    const PaymentWrap = this.state.paymentType === 'adyenPayment' ? PaymentWrapperAdvanced : PaymentWrapper;
    const paymentStepNumber = this.props.cart.isDigital ? 2 : 3;

    return (
      <Page>
        {this.props.cart?.items?.length === 0 ? (
          <EmptyCartWrap
            css={{
              background: {
                image: setImageBackground(selectDeviceImageFromProperty(this.props.page.emptyCartImage, this.props.page.emptyCartImageSmallScreen, isMobile,)?.url, Preset.FullWidth),
              },
            }}
          >
            {(combinedErrors?.length ?? 0) > 0 && checkoutErrorModal}
            <EmptyCartGridWrap className="grid">
              <div
                className={createGridClasses(
                  12,
                  8,
                  createOffsetGridClassLarge(3) + (!isMobile ? ' grid subgrid-8' : ''),
                )}
              >
                <EmptyCartInnerWrap className={createGridClasses(12, 8)}>
                  <EmptyCartHeading fontSize={ResponsiveSize.D96_M40}>
                    {epiPropertyValue(this.props.page.emptyCartHeading)}
                  </EmptyCartHeading>
                  {emptyCartIntroText && (
                    <EmptyCartParagraph fontSize={ResponsiveSize.D32_M24}>{emptyCartIntroText}</EmptyCartParagraph>
                  )}
                  {emptyCartButton && emptyCartButton.text && (
                    <EmptyCartButtonWrap>
                      <EmptyCartLink
                        text={emptyCartButton.text}
                        title={emptyCartButton.title}
                        to={emptyCartButton.url}
                      />
                    </EmptyCartButtonWrap>
                  )}
                </EmptyCartInnerWrap>
              </div>
            </EmptyCartGridWrap>
            <EpiProperty for={this.props.page.emptyCartContentArea} />
          </EmptyCartWrap>
        ) : (
          <GridWrap>
            <Form
              elementRef={(el) => (this.formElement = el)}
              onSubmit={(e) => this.onCompletePurchase(e)}
              onKeyPress={(e) => this.onFormKeyPress(e)}
              className="grid"
            >
              <FormContent className={createGridClasses(12, 7, !isMobile ? 'grid subgrid-7' : '')}>
                <FormContentInner className={createGridClasses(12, 6)}>
                  <div className="grid">
                    <div className={createGridClasses(12, 10, createCenterGridClass(2, 11, GridSize.LG))}>
                      <FormContentHeading fontSize={ResponsiveSize.D16_M16}>
                        {this.props.labels.cartHeading}
                      </FormContentHeading>
                      {(combinedErrors?.length ?? 0) > 0 && checkoutErrorModal}
                      <FullCart
                        labels={this.props.labels}
                        backupStore={this.props.backupStore}
                        isMobile={isMobile}
                        cart={this.props.cart}
                        validationItems={this.state.validationItems}
                      />
                      {this.props.backupStore?.enabled &&
                        this.props.backupStore?.backupInventoryOrderMessage &&
                        shippingNoteBox}

                      {!isMobile && recommendations}
                      {!this.props.isLoggedIn && !isMobile && registerContent}
                      {isMobile && summary}
                      {isMobile && recommendations}
                      {isMobile && memberContent}
                      {!this.props.isLoggedIn && isMobile && registerContent}
                      <PaymentWrap elementRef={(el) => (this.paymentWidgetElement = el)}>
                        {this.state.shippingSelector && <>
                          <CheckoutStepHeader>
                            <CheckoutStepTitle
                              css={{
                                ...(this.state.activeCheckoutStep !== 1 && {
                                  color: style.colors.monochrome.a11ygrey
                                })
                              }}
                              fontSize={ResponsiveSize.D20_M20}>
                              {this.props.addresses.labels.heading}
                            </CheckoutStepTitle>
                            <CheckoutStepEdit
                              to={null}
                              onClick={() => {
                                this.setState({ activeCheckoutStep: 1 });
                                return false;
                              }}
                              css={{
                                ...((this.state.completedCheckoutStep < 1 || this.state.activeCheckoutStep == 1) && {
                                  display: 'none'
                                })
                              }}
                              title={this.props.labels.edit + " " + this.props.addresses.labels.heading.toLowerCase()}
                            >
                              {this.props.labels.edit}
                            </CheckoutStepEdit>
                          </CheckoutStepHeader>
                          <CheckoutStepContent
                            css={{
                              ...(this.state.activeCheckoutStep === 1 && {
                                position: 'static',
                                visibility: 'visible',
                                height: 'auto'
                              })
                            }}
                          >
                            <CustomerDetails
                              shippingAddress={this.state.customerAddresses.shippingAddress}
                              billingAddress={this.state.customerAddresses.billingAddress}
                              useSeparateShippingAddress={this.state.customerAddresses.separateShippingAddress}
                              addressLabels={this.props.addresses.labels}
                              regex={this.props.regex}
                              //onPreUpdate={() => this.setState({ allowCheckoutCompletion: false })}
                              onPreUpdate={() => { }}
                              onUpdate={this.handleCustomerDetailsUpdate}
                              continueToDeliveryLabel={paymentStepNumber === 2 ? this.props.shipping.labels.continueToPayment : this.props.addresses.labels.continueToDelivery}
                              errorMessage={this.state.customerDetailsError}
                            />
                          </CheckoutStepContent>
                          {!this.props.cart.isDigital && <>
                            <CheckoutStepHeader ref={(el) => (this.shippingRef = el)}>
                              <CheckoutStepTitle
                                css={{
                                  ...(this.state.activeCheckoutStep !== 2 && {
                                    color: style.colors.monochrome.a11ygrey
                                  })
                                }}
                                fontSize={ResponsiveSize.D20_M20}>
                                {this.props.shipping.labels.heading}
                              </CheckoutStepTitle>
                              <CheckoutStepEdit
                                to={null}
                                onClick={() => {
                                  this.setState({ activeCheckoutStep: 2 });
                                  return false;
                                }}
                                css={{
                                  ...((this.state.completedCheckoutStep < 2 || this.state.activeCheckoutStep == 2) && {
                                    display: 'none'
                                  })
                                }}
                                title={this.props.labels.edit + " " + this.props.shipping.labels.heading.toLowerCase()}
                              >
                                {this.props.labels.edit}
                              </CheckoutStepEdit>
                            </CheckoutStepHeader>
                            <CheckoutStepContent
                              css={{
                                ...(this.state.activeCheckoutStep === 2 && {
                                  position: 'static',
                                  visibility: 'visible',
                                  height: 'auto'
                                }),
                                ...(shippingLock && {
                                  pointerEvents: 'none',
                                  opacity: 0.5,
                                  filter: 'blur(1px)',
                                }),
                              }}
                            >
                              <ShippingMethods
                                postalCode={this.state.customerAddresses.separateShippingAddress ? this.state.customerAddresses.shippingAddress.postalCode : this.state.customerAddresses.billingAddress.postalCode}
                                subTotal={this.props.cart.subTotal}
                                onSelectShippingMethod={this.handleShippingOptionUpdate}
                                onNextStep={() => {
                                  this.setState({ activeCheckoutStep: paymentStepNumber });
                                }}
                                buttonText={this.props.shipping.labels.continueToPayment}
                                language={this.props.language}
                                onError={() => { }}
                                updateErrorMessage={this.state.shippingOptionError}
                                updateInvalid={this.state.shippingOptionInvalid}
                              />
                            </CheckoutStepContent>
                          </>}
                        </>}

                        {this.state.paymentType === 'adyenPayment' && (
                          <>
                            <CheckoutStepHeader ref={(el) => (this.paymentRef = el)}>
                              <CheckoutStepTitle
                                css={{
                                  ...(this.state.activeCheckoutStep !== paymentStepNumber && {
                                    color: style.colors.monochrome.a11ygrey
                                  })
                                }}
                                fontSize={ResponsiveSize.D20_M20}>
                                {this.props.payment.labels.heading}
                              </CheckoutStepTitle>
                              <CheckoutStepEdit
                                to={null}
                                onClick={() => {
                                  this.setState({ activeCheckoutStep: paymentStepNumber });
                                  return false;
                                }}
                                css={{
                                  ...(this.state.completedCheckoutStep < paymentStepNumber && {
                                    display: 'none'
                                  })
                                }}
                                title={this.props.labels.edit + " " + this.props.payment.labels.heading.toLowerCase()}
                              >
                                {this.props.labels.edit}
                              </CheckoutStepEdit>
                            </CheckoutStepHeader>
                            <CheckoutStepContent
                              css={{
                                ...(this.state.activeCheckoutStep === paymentStepNumber && {
                                  position: 'static',
                                  visibility: 'visible',
                                  height: 'auto'
                                })
                              }}
                            >
                              <AdyenPayment
                                widgetData={this.state.paymentWidgetData}
                                allowCompletion={this.state.allowCheckoutCompletion}
                                onLoaded={() => {
                                  scrollTo(this.paymentRef.offsetTop - 20, 500);
                                }}
                                sessionId={this.state.paymentSessionId}
                                active={this.state.activeCheckoutStep === paymentStepNumber}
                              />
                            </CheckoutStepContent>
                          </>
                        )}

                        {this.state.paymentType === 'klarnaCheckout' && this.state.paymentWidgetData && (
                          <InteractivePaymentGatewayAdapter
                            snippet={this.state.paymentWidgetData}
                            isLoading={this.props.isLoading}
                            onInfoChanged={(c) => { }}
                            //onPaymentFailed={this.showErrorModal}
                            email={this.props.customer.email}
                            ref={(c) => (this.interactiveGateway = c)}
                          />
                          // <styled.Div
                          //   css={{
                          //     ...(shouldKlarnaLock && {
                          //       pointerEvents: 'none',
                          //       opacity: 0.6,
                          //       filter: 'blur(2px)',
                          //     }),
                          //   }}
                          // >

                          // </styled.Div>
                        )}
                      </PaymentWrap>
                    </div>
                  </div>
                </FormContentInner>
              </FormContent>
              {!isMobile && (
                <RightCol className={createGridClasses(12, 5, createOffsetGridClassLarge(8))}>
                  <RightColSticky>
                    {memberContent}
                    {summary}
                  </RightColSticky>
                </RightCol>
              )}
            </Form>
          </GridWrap>
        )
        }
      </Page>
    );
  }
}
export default connect(
  (state): ConnectedStateType => ({
    language: state.appShellData.market.language,
    isLoggedIn: !!state.currentUser && state.currentUser.isLoggedIn,
    cart: state.cart,
    currentBreakpoint: state.currentBreakpoint
  }),
  (dispatch): ConnectedDispatchType => ({
    onKlarnaShippingOptionChange(args: KlarnaCheckoutShippingOptionChangeEventArgs) {
      return dispatch(onKlarnaShippingOptionChange(args));
    },
    setAddresses(shippingAddress: AddressType, billingAddress: AddressType, useSeparateShippingAddress: boolean) {
      return dispatch(setAddresses(shippingAddress, billingAddress, useSeparateShippingAddress));
    },
    setShippingMethod(method: ShippingMethodViewModelType) {
      return dispatch(setShippingMethod(method));
    },
    sendPendingCheckoutRequests() {
      return dispatch(sendPendingCheckoutRequests());
    },
    addGiftCardCode(cardNumber: string) {
      return dispatch(addGiftCardCode(cardNumber));
    },
    removeGiftCardCode(cardNumber: string) {
      return dispatch(removeGiftCardCode(cardNumber));
    },
    addDiscountCode(code: string) {
      return dispatch(addDiscountCode(code));
    },
    removeDiscountCode(code: string) {
      return dispatch(removeDiscountCode(code));
    },
    setApplyMembership(value: boolean) {
      return dispatch(setApplyMembership(value));
    },
    /*  setGiftWrapping(value: boolean) {
      return dispatch(setGiftWrapping(value));
    }, */
    openAccountBox(scene?: AccountBoxScene) {
      dispatch(openAccountBox(scene));
    },
    addVoucher(id: string, type: VoucherType) {
      return dispatch(addVoucher(id, type));
    },
    removeVoucher(id: string, type: VoucherType) {
      return dispatch(removeVoucher(id, type));
    },
  }),
)(
  connectWithFeedback({
    minimumPending: 750,
    maximumRejected: 500,
    maximumFulfilled: 500,
  })(CheckoutPage),
);
